import React, { useState } from 'react'
import {
  PREFILL_EDIT_FIELD_GROUPS,
  PREFILL_EDIT_FIELD_TYPE_LABELS,
  PREFILL_EDIT_FIELD_TYPES,
  PrefillEditFieldDefinition,
  createPrefillEditField,
  defaultOptionsForEditFieldType,
  isPrefillEditFieldRequired
} from './prefillFieldSettingsUtils'
import { resolvePrefillEditFieldId } from './tableFieldUtils'

const optionsToText = (options?: Array<{ text: string }>): string =>
  (options || []).map((option) => option.text).join(', ')

const parseOptionsText = (text: string): Array<{ text: string; value: string }> =>
  text
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => ({ text: part, value: part }))

const getOptionsDraftKey = (
  parentFieldId: number,
  index: number,
  rowIndex?: number,
  columnIndex?: number
): string => `${parentFieldId}-${rowIndex ?? 'x'}-${columnIndex ?? 'x'}-${index}`

type FieldsEditorVariant = 'prefill-edit' | 'conditional-render'

const VARIANT_COPY: Record<
  FieldsEditorVariant,
  { badge: string; emptyHint: string; requiredSectionTitle: string }
> = {
  'prefill-edit': {
    badge: 'Edit-only field',
    emptyHint: 'Add fields that appear only when the recipient clicks Edit.',
    requiredSectionTitle: 'isRequired'
  },
  'conditional-render': {
    badge: 'Conditional field',
    emptyHint: 'Add fields that appear when the condition above matches.',
    requiredSectionTitle: 'isRequired'
  }
}

interface Props {
  parentFieldId: number
  editFields: PrefillEditFieldDefinition[]
  onChange: (editFields: PrefillEditFieldDefinition[]) => void
  columnIndex?: number
  rowIndex?: number
  variant?: FieldsEditorVariant
}

const PrefillEditFieldsEditor: React.FC<Props> = ({
  parentFieldId,
  editFields,
  onChange,
  columnIndex,
  rowIndex,
  variant = 'prefill-edit'
}) => {
  const copy = VARIANT_COPY[variant]
  const [optionsDrafts, setOptionsDrafts] = useState<Record<string, string>>({})

  const clearOptionsDraft = (draftKey: string) => {
    setOptionsDrafts((prev) => {
      if (!(draftKey in prev)) return prev
      const next = { ...prev }
      delete next[draftKey]
      return next
    })
  }

  const updateField = (index: number, updates: Partial<PrefillEditFieldDefinition>) => {
    const next = [...editFields]
    next[index] = { ...next[index], ...updates }
    onChange(next)
  }

  const changeFieldType = (index: number, type: string) => {
    const current = editFields[index]
    clearOptionsDraft(getOptionsDraftKey(parentFieldId, index, rowIndex, columnIndex))
    updateField(index, {
      type,
      options: defaultOptionsForEditFieldType(type),
      accept: type === 'file' ? current.accept || '.pdf,.doc,.docx,.jpg,.jpeg,.png' : undefined
    })
  }

  const removeField = (index: number) => {
    onChange(editFields.filter((_, i) => i !== index))
  }

  const addField = (type: string) => {
    onChange([
      ...editFields,
      createPrefillEditField(parentFieldId, editFields, type, columnIndex, rowIndex)
    ])
  }

  return (
    <div className='prefill-edit-fields-editor'>
      {editFields.length === 0 ? (
        <p className='text-muted small mb-2'>{copy.emptyHint}</p>
      ) : (
        editFields.map((field, index) => {
          const resolvedFieldId = resolvePrefillEditFieldId(
            field,
            index,
            rowIndex !== undefined || columnIndex !== undefined ? undefined : parentFieldId
          )
          const fieldIsRequired = isPrefillEditFieldRequired(field)
          const optionsDraftKey = getOptionsDraftKey(parentFieldId, index, rowIndex, columnIndex)
          const requiredToggleId = `edit-field-required-${parentFieldId}-${rowIndex ?? 'x'}-${
            columnIndex ?? 'x'
          }-${index}`

          return (
            <div
              key={`edit-field-${parentFieldId}-${rowIndex ?? 'x'}-${
                columnIndex ?? 'x'
              }-${index}-${resolvedFieldId}`}
              className='border rounded p-2 mb-2 bg-white'
            >
              <div className='d-flex justify-content-between align-items-center mb-2'>
                <span
                  className={`badge ${
                    variant === 'conditional-render' ? 'bg-success' : 'bg-warning text-dark'
                  }`}
                >
                  {copy.badge}
                </span>
                <button
                  type='button'
                  className='btn btn-sm btn-light-danger'
                  onClick={() => removeField(index)}
                >
                  Remove
                </button>
              </div>
              <div className='mb-2'>
                <label className='form-label'>Type</label>
                <select
                  className='form-select form-select-sm'
                  value={field.type}
                  onChange={(e) => changeFieldType(index, e.target.value)}
                >
                  {PREFILL_EDIT_FIELD_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {PREFILL_EDIT_FIELD_TYPE_LABELS[type]}
                    </option>
                  ))}
                </select>
              </div>
              <div className='mb-2'>
                <label className='form-label'>Label</label>
                <input
                  type='text'
                  className='form-control form-control-sm'
                  value={field.label}
                  onChange={(e) => updateField(index, { label: e.target.value })}
                />
              </div>

              <div className='mt-3 mb-2'>
                <div className='d-flex justify-content-between align-items-center'>
                  <h6 className='mb-0'>{copy.requiredSectionTitle}</h6>
                  <div className='form-check form-switch mb-0'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      id={requiredToggleId}
                      checked={fieldIsRequired}
                      onChange={(e) =>
                        updateField(index, {
                          isRequired: e.target.checked,
                          requiredErrorMessage: e.target.checked
                            ? field.requiredErrorMessage || 'This field is required'
                            : field.requiredErrorMessage
                        })
                      }
                    />
                    <label className='form-check-label' htmlFor={requiredToggleId}>
                      {fieldIsRequired ? 'Required' : 'Optional'}
                    </label>
                  </div>
                </div>
                {fieldIsRequired && (
                  <div className='mt-2'>
                    <label className='form-label'>Validation Message</label>
                    <input
                      type='text'
                      className='form-control form-control-sm'
                      value={field.requiredErrorMessage || ''}
                      placeholder='This field is required'
                      onChange={(e) => updateField(index, { requiredErrorMessage: e.target.value })}
                    />
                  </div>
                )}
              </div>

              {!['file', 'switch', 'radio', 'checkbox', 'select'].includes(field.type) && (
                <div className='mb-2'>
                  <label className='form-label'>Placeholder</label>
                  <input
                    type='text'
                    className='form-control form-control-sm'
                    value={field.placeholder || ''}
                    onChange={(e) => updateField(index, { placeholder: e.target.value })}
                  />
                </div>
              )}
              {field.type === 'file' && (
                <div>
                  <label className='form-label'>Accepted file types</label>
                  <input
                    type='text'
                    className='form-control form-control-sm'
                    placeholder='.pdf,.jpg,.png'
                    value={field.accept || ''}
                    onChange={(e) => updateField(index, { accept: e.target.value })}
                  />
                </div>
              )}
              {(field.type === 'select' || field.type === 'radio' || field.type === 'checkbox') && (
                <div className='mt-2'>
                  <label className='form-label'>Options (comma separated)</label>
                  <input
                    type='text'
                    className='form-control form-control-sm'
                    value={optionsDrafts[optionsDraftKey] ?? optionsToText(field.options)}
                    onChange={(e) => {
                      const nextText = e.target.value
                      setOptionsDrafts((prev) => ({ ...prev, [optionsDraftKey]: nextText }))
                      updateField(index, { options: parseOptionsText(nextText) })
                    }}
                    onBlur={(e) => {
                      updateField(index, { options: parseOptionsText(e.target.value) })
                      clearOptionsDraft(optionsDraftKey)
                    }}
                  />
                </div>
              )}
            </div>
          )
        })
      )}

      {PREFILL_EDIT_FIELD_GROUPS.map((group) => (
        <div key={group.label} className='mb-2'>
          <div className='text-muted small mb-1'>{group.label}</div>
          <div className='d-flex flex-wrap gap-2'>
            {group.types.map((type) => (
              <button
                key={type}
                type='button'
                className='btn btn-sm btn-light-primary'
                onClick={() => addField(type)}
              >
                + {PREFILL_EDIT_FIELD_TYPE_LABELS[type]}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

export default PrefillEditFieldsEditor
